import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchCurrentUserMembership,
  selectCurrentUser,
  selectCurrentUserMembership,
  selectCurrentUserMembershipError,
  selectCurrentUserMembershipFetchInProgress,
  selectCurrentUserMembershipResolved,
} from 'ducks/user.duck';
import { hasMembership } from 'util/userHelpers';

// A bare `currentUserMembership === null` covers three different situations,
// and pages that gate on membership kept conflating them (CH-460 follow-up):
//
//   not asked yet | the fetch is dispatched from fetchCurrentUser's callback,
//                 | so nothing is in flight *and* nothing is known
//   in flight     | same null, still no answer
//   answered      | null here really does mean "not an organisation member"
//   failed        | asked, no answer, and none is coming
//
// This hook collapses them into one status a caller can switch on, and retries
// a failed lookup once per mount so a single transient 500 doesn't leave every
// membership-gated page guessing for the rest of the session.
export const MEMBERSHIP_PENDING = 'pending';
export const MEMBERSHIP_MEMBER = 'member';
export const MEMBERSHIP_NOT_MEMBER = 'not-member';
export const MEMBERSHIP_UNKNOWN = 'unknown';

const useCurrentUserMembership = () => {
  const dispatch = useDispatch();
  const currentUser = useSelector(selectCurrentUser);
  const membership = useSelector(selectCurrentUserMembership);
  const resolved = useSelector(selectCurrentUserMembershipResolved);
  const fetchInProgress = useSelector(selectCurrentUserMembershipFetchInProgress);
  const error = useSelector(selectCurrentUserMembershipError);
  const currentUserShowError = useSelector((state) => state.user.currentUserShowError);
  const hasRetried = useRef(false);

  // The membership fetch hangs off currentUser.show, so if that failed there is
  // no lookup to wait for and 'pending' would never end.
  const userShowFailed = !currentUser && !!currentUserShowError;

  const canRetry = !!currentUser && !!error && !resolved && !fetchInProgress;

  useEffect(() => {
    if (!canRetry || hasRetried.current) return;
    hasRetried.current = true;
    dispatch(fetchCurrentUserMembership());
  }, [canRetry, dispatch]);

  let status = MEMBERSHIP_PENDING;
  if (resolved) {
    status = hasMembership(membership) ? MEMBERSHIP_MEMBER : MEMBERSHIP_NOT_MEMBER;
  } else if (userShowFailed || (error && hasRetried.current && !fetchInProgress)) {
    status = MEMBERSHIP_UNKNOWN;
  }

  return { membership, status };
};

export default useCurrentUserMembership;
